import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { ActionsMenu } from "../../../../../../shared/components/table/custom-actions";
import ActionsModalPopup, {
  ActionButtonConfig,
} from "../../../../../../shared/components/modals/action-popup";
import CustomStepper from "../../../../../../shared/components/stepper/steppers";
import {
  mapToScopeCertificateForm,
  ScopeCertificateDates,
  ScopeCertificateDetails,
  ScopeCertificateForm,
  scopeCertificateFormToMap,
  StandardDetails,
  NonMandatoryCriterionDetails,
} from "../../../sc-models/scope-certificate-details-dto";
import ScopeCertificateDetailsForm from "./scope-certificate-details-form";
import ScopeCertificateDatesForm from "./scope-certificate-dates-form";
import StandardDetailsForm from "./standard-details-form";
import NonMandatoryCriterionDetailsForm from "./non-mandatory-criterion-details-form";
import { FooterContainer } from "../../../../../../shared/components/modals/custom-popup";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { useNavigate, useParams } from "react-router-dom";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { useScStore } from "../../../../../../store/scStandard.store";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";

const initScopeCertificateDetails: ScopeCertificateDetails = {
  scopeCertificateNo: "",
  brandName: [],
  gotsSCNo: "",
  scLegacyNo: "",
  scCBAuditorId: [],
  scNumberOfFarms: null, // Moved from StandardDetails
  isFarmSC: "", // "Y" or "N" — empty until user selects
  scCBLicensingCode: "",
  scVersionNo: "",
  scProductLimitations: "",
  scCreationTimestamp: "",
  scCbLicensingCodeDescription: "",
  ccsFlag: "",
  stdClaimsPolicy: "",
  certifiedOrganizationApprovedClaims: ""
};

const initScopeCertificateDates: ScopeCertificateDates = {
  scStatus: "",
  scStatusDescription: "",
  scSuspensionReason: "",
  scSuspensionReasonDescription: "",
  scCbAccreditationNo: "",
  scCreatedDate: new Date().toISOString().split('T')[0],
  scLastUpdatedDate: "",
  scExtendedUntilDate: "",
  scLastAuditStartDate: "",
  scValidFromDate: "",
  scValidUntilDate: "",
  scWithdrawalDate: "",
  scLastAuditDate: "",
  scStateOrProvince: "",
  scStateOrProvinceDescription: "",
  scPlaceOfIssue: "",
  scPlaceOfIssueDescription: "",
  scCbAccreditationBody: "",
  scCbAccreditationBodyDescription: "",
  scCbScopeStipulations: "",
};

const initScopeCertificateForm: ScopeCertificateForm = {
  scopeCertificateDetails: initScopeCertificateDetails,
  standardDetails: [],
  scopeCertificateDates: initScopeCertificateDates,
  scNonMandatoryCriterionInformation: [],
};

interface ScopeCertificateComponentProps {
  onNextTab: () => Promise<void>;
}

const ScopeCertificateComponent: React.FC<ScopeCertificateComponentProps> = ({ onNextTab }) => {

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [data, setData] = useState<ScopeCertificateForm[]>([]);
  const [scopeCertificateData, setScopeCertificateData] =
    useState<ScopeCertificateForm>(initScopeCertificateForm);
  const { executeGet, executePost, loading } = useApi();
  const scopeDetailsRef = useRef<any>(null);
  const standardDetailsRef = useRef<any>(null);
  const certificateDatesRef = useRef<any>(null);
  const nonMandatoryCriterionDetailsRef = useRef<any>(null);
  const { isRestrictPopUpSave, setRestrictPopUpSave, cbLicensingCode } = useTransactionCertificateStore();
  const navigate = useNavigate();
  const { notify } = useNotification()
  const { formtype, SC_ID } = useParams();
  const { editSCInfo, setScStandards, setScStandardDescriptions } = useScStore();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");
  const [existingSCData, setExistingSCData] = useState<any>(null);

  const { setEditSCInfo } = useScStore();

  const [hasPR0035, setHasPR0035] = useState<boolean>(false);
  const [hasFarmProcessCodes, setHasFarmProcessCodes] = useState<boolean>(false);
  const [hasPR0037, setHasPR0037] = useState<boolean>(false);

  // Suspended SCs: only scStatus can change; all other fields are read-only
  const isSuspended = scopeCertificateData.scopeCertificateDates.scStatus === 'S';

  const [facilityMinAuditDate, setFacilityMinAuditDate] = useState<string>("");
  const [facilityMaxAuditDate, setFacilityMaxAuditDate] = useState<string>("");
  const [isFarmSC, setIsFarmSC] = useState<boolean>(false);

  const fetchScopeCerificateDetails = async () => {
    const result = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
    const data = mapToScopeCertificateForm(result?.data);
    const standards = result?.data?.scStandardInformation?.map(
      (item: any) => item.scStandard
    ) || [];
    setScStandards(standards);
    // Build code→description map for the detail panel to use
    const descMap: Record<string, string> = {};
    (result?.data?.scStandardInformation || []).forEach((item: any) => {
      if (item.scStandard && item.scStandardDescription) {
        descMap[item.scStandard] = item.scStandardDescription;
      }
    });
    setScStandardDescriptions(descMap);
    setScopeCertificateData(data);
    setExistingSCData(data);
    // Restore isFarmSC local state from the fetched value so the radio re-selects correctly
    if (data.scopeCertificateDetails.isFarmSC) {
      setIsFarmSC(data.scopeCertificateDetails.isFarmSC === 'Y');
    }

    // Fetch facility process codes for cross-tab validation
    try {
      // GET_FACILITY_LIST_DETAILS_V4 returns all facilities for an SC (only needs scId).
      // GET_FACILITY_DETAILS_V4 is the single-facility endpoint that requires facilityId in header.
      const facilityResponse = await executeGet(API_ROUTES.GET_FACILITY_LIST_DETAILS_V4, { scId: SC_ID });
      let pr0035Found = false;
      let farmProcessFound = false;
      let pr0037Found = false;
      let hasOtherFarmCodes = false;

      // GET_FACILITY_LIST_DETAILS_V4 wraps data as: { response: { facilityDetails: [...] } }
      const facilities: any[] = facilityResponse?.data?.response?.facilityDetails || [];

      if (Array.isArray(facilities) && facilities.length > 0) {
        const INVALID_AUDIT_DATE = '1900-01-01';
        const auditDates: string[] = [];

        facilities.forEach((facility: any) => {
          // Collect valid facilityLastAuditDate values (ignore 1900-01-01 = not yet audited)
          if (facility.facilityLastAuditDate && facility.facilityLastAuditDate !== INVALID_AUDIT_DATE) {
            auditDates.push(facility.facilityLastAuditDate);
          }

          if (facility.facilityProcessDetails && Array.isArray(facility.facilityProcessDetails)) {
            const facilityProcessCodes = facility.facilityProcessDetails.map((p: any) => p.facilityProcessCategoryCode);
            if (facilityProcessCodes.includes("PR0035")) pr0035Found = true;
            if (facilityProcessCodes.includes("PR0037")) pr0037Found = true;

            const farmCodes = ["PR0011", "PR0036", "PR0038"];
            const comboCodes = ["PR0002", "PR0014", "PR0026"];

            const hasFarm = farmCodes.some(c => facilityProcessCodes.includes(c));
            const hasCombo = comboCodes.some(c => facilityProcessCodes.includes(c));
            const hasOther = facilityProcessCodes.some((c: string) => !farmCodes.includes(c) && !comboCodes.includes(c) && c !== "PR0037");

            if (hasFarm) {
              if (hasCombo || hasOther || facilityProcessCodes.every((c: string) => farmCodes.includes(c))) {
                farmProcessFound = true;
              }
            }
          }
        });

        // Compute min/max audit dates from all valid facility audit dates
        if (auditDates.length > 0) {
          const sorted = [...auditDates].sort();
          setFacilityMinAuditDate(sorted[0]);
          setFacilityMaxAuditDate(sorted[sorted.length - 1]);
        }
      }

      setHasPR0035(pr0035Found);
      setHasPR0037(pr0037Found);
      setHasFarmProcessCodes(farmProcessFound && !pr0037Found);

    } catch (error) {
      console.error("Error fetching facility details for validation:", error);
    }
  };
  // Reset any stale TC restriction flag so the Next button is never blocked on SC form load
  useEffect(() => {
    setRestrictPopUpSave(false);
  }, []);

  useEffect(() => {
    if (SC_ID) fetchScopeCerificateDetails();
  }, []);

  // M5: Auto-derive scStatus from dates for new SC (Create workflow only)
  // Rule: validFromDate > today → 'I' (Issued); today in [validFrom, validUntil/extendedUntil] → 'V' (Valid); past end → 'E' (Expired)
  useEffect(() => {
    if (SC_ID) return; // Existing SCs use M3 transition machine — do not override

    const { scValidFromDate, scValidUntilDate, scExtendedUntilDate } =
      scopeCertificateData.scopeCertificateDates;

    if (!scValidFromDate) return; // Dates not yet filled — nothing to derive

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const validFrom = new Date(scValidFromDate);
    const validUntil = scValidUntilDate ? new Date(scValidUntilDate) : null;
    const extendedUntil = scExtendedUntilDate ? new Date(scExtendedUntilDate) : null;

    let derivedStatus: string;
    if (validFrom > today) {
      derivedStatus = 'I'; // SC has not started yet → Issued
    } else {
      // SC has started — check expiry (prefer extendedUntil over validUntil if present)
      const effectiveEndDate = extendedUntil ?? validUntil;
      derivedStatus = effectiveEndDate && today > effectiveEndDate ? 'E' : 'V';
    }

    // Only update when status has actually changed to prevent infinite re-render
    if (derivedStatus !== scopeCertificateData.scopeCertificateDates.scStatus) {
      handleDataUpdate('scopeCertificateDates', {
        ...scopeCertificateData.scopeCertificateDates,
        scStatus: derivedStatus,
      });
    }
  }, [
    scopeCertificateData.scopeCertificateDates.scValidFromDate,
    scopeCertificateData.scopeCertificateDates.scValidUntilDate,
    scopeCertificateData.scopeCertificateDates.scExtendedUntilDate,
    scopeCertificateData.scopeCertificateDates.scStatus,
    SC_ID
  ]);


  const toggleDrawer = () => {
    setActiveStep(0);
    setDrawerOpen((prev) => !prev);
  };

  const handleDataUpdate = (stepKey: string, updatedData: any) => {
    setScopeCertificateData((prev) => ({
      ...prev,
      [stepKey]: updatedData,
    }));
  };
  // formRefs order MUST match the steps array order:
  // [0] Scope Certificate Info, [1] Certificate Dates & Accreditation, [2] Standard Info (table – null), [3] Non-mandatory Criterion (table – null)
  const formRefs = [scopeDetailsRef, certificateDatesRef, standardDetailsRef, nonMandatoryCriterionDetailsRef]
  /**
   * Validates that the YYMM portion of the SC Number matches the year+month
   * of the SC Valid From Date. Returns an error message string, or null if valid.
   *
   * This is a cross-step check (SC Number = step 1, Valid From Date = step 2),
   * so it lives in navigation logic rather than in either step's Yup schema.
   */
  const checkYymmConsistency = (scNo?: string, validFromDate?: string | number | null): string | null => {
    if (!scNo || !validFromDate) return null; // Either not yet filled — skip
    const match = scNo.match(/^SC-\d{8}-(\d{2})(\d{2})-\d{2}$/);
    if (!match) return null; // Format is wrong, Yup handles that separately
    const scYY = match[1];
    const scMM = match[2];
    const fromDate = new Date(String(validFromDate));
    if (isNaN(fromDate.getTime())) return null; // Invalid date — Yup handles that
    const fromYY = String(fromDate.getFullYear()).slice(-2);
    const fromMM = String(fromDate.getMonth() + 1).padStart(2, '0');
    if (scYY !== fromYY || scMM !== fromMM) {
      return `YYMM in SC Number (${scYY}${scMM}) must match the Year and Month of SC Valid From Date (${fromYY}${fromMM}). Please go back to Step 1 and update the SC Number.`;
    }
    return null;
  };

  const handleNext = async () => {


    if (activeStep === 3) {
      // Step 3: Non-mandatory Criterion Details (table-based, no form ref)
      if (scopeCertificateData.scNonMandatoryCriterionInformation.length > 0) {
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } else {
        notify('Please add at least one Non-mandatory Criterion Detail', 'error');
        return;
      }
    } else if (activeStep === 2) {
      // Step 2: Standard Information (table-based, no form ref)
      if (scopeCertificateData.standardDetails.length > 0) {
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } else {
        notify('Please add at least one Standard Detail', 'error');
        return;
      }
    } else if (activeStep === 1 && certificateDatesRef?.current?.values?.scExtendedUntilDate) {
      // Step 1: Certificate Dates — warn if extended date is set
      setDialogOpen(true);
      setPopUpTitle("Warning");
      setPopUpMessage("SC extension requires Textile Exchange approval. Proceeding assumes that approval is obtained.");
      setPopUpBtnName("Confirm");
      return;
    } else if (formRefs[activeStep]?.current) {
      // Steps with Formik forms (0: SC Info, 1: Certificate Dates)
      const formik = formRefs[activeStep].current;

      // submitForm() always increments Formik's submitCount even when validation fails.
      // This is the trigger that makes hasError=true for all invalid fields (red highlight + error text).
      await formik.submitForm();

      // Now check errors to decide whether to proceed
      const errors = await formik.validateForm();
      if (Object.keys(errors).length === 0) {

        // ── Cross-validation: BrandName required when PR0035 is present ──────────
        // Formik caches the validationSchema reference internally. When hasPR0035
        // flips true after the async facility fetch, Formik still runs the original
        // schema (hasPR0035=false), so the Yup brandName rule never fires.
        // We guard explicitly here using the live React state value instead.
        if (activeStep === 0 && hasPR0035) {
          const brandNames: string[] = formik.values?.brandName || [];
          if (brandNames.length === 0) {
            // Touch the field so FormBuilderV4 shows the red error border
            formik.setFieldTouched('brandName', true, false);
            notify('Brand Name is required when process code PR0035 is present', 'error');
            return;
          }
        }

        // Cross-step YYMM check — runs only when leaving Step 1 (Certificate Dates)
        // because that is the FIRST moment when both SC Number (step 0) AND
        // Valid From Date (step 1) are definitively known.
        // Kept out of Yup to avoid false red-field errors when navigating Back.
        if (activeStep === 1) {
          const yymmError = checkYymmConsistency(
            scopeCertificateData.scopeCertificateDetails?.scopeCertificateNo,
            formik.values?.scValidFromDate
          );
          if (yymmError) {
            notify(yymmError, 'error');
            return;
          }
        }
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } else {
        notify('Please fill in all required fields before proceeding', 'error');
      }
    }
  };
  const handleFinish = async () => {

    if (activeStep === 3) {
      // For Non-mandatory Criterion Details tab (table-based, no form ref)
      if (scopeCertificateData.scNonMandatoryCriterionInformation.length > 0) {
        // Call the post function with updated data
        await postScopeCertificateDetails(scopeCertificateData);
      } else {
        notify('Please add at least one Non-mandatory Criterion Detail', 'error');
      }
    } else if (formRefs[activeStep]?.current) {
      // For other tabs with form refs
      try {
        const formik = formRefs[activeStep].current;

        // submitForm() always increments Formik's submitCount even when validation fails.
        // This is the trigger that makes hasError=true for all invalid fields (red highlight + error text).
        await formik.submitForm();

        const errors = await formik.validateForm();
        if (Object.keys(errors).length === 0) {
          // Call the post function with updated data
          await postScopeCertificateDetails(scopeCertificateData);
        } else {
          notify('Please fill in all required fields before proceeding', 'error');
        }
      } catch (error) {
        console.error("[handleFinish] Error during form submission or validation:", error);
        notify('An error occurred during form submission', 'error');
      }
    }
  };


  const postScopeCertificateDetails = async (updatedScopeCertificateData: ScopeCertificateForm) => {
    try {
      // Validate scNumberOfFarms when farm-related standards are selected
      const farmRelatedStandards = ['RWS', 'RMS', 'RAS', 'RDS', 'MMS'];
      const selectedStandards = updatedScopeCertificateData.standardDetails?.map(detail => detail.scStandard) || [];
      const hasFarmRelatedStandards = selectedStandards.some(standard => farmRelatedStandards.includes(standard));
      
      if (hasFarmRelatedStandards) {
        const scNumberOfFarms = updatedScopeCertificateData.scopeCertificateDetails?.scNumberOfFarms;
        if (!scNumberOfFarms || scNumberOfFarms === null || scNumberOfFarms === 0) {
          notify('SC Number of Farms is required when farm-related standards (RWS, RMS, RAS, RDS, MMS) are selected', 'error');
          return;
        }
      }

      const payload = scopeCertificateFormToMap(updatedScopeCertificateData, SC_ID, cbLicensingCode);
      
      const queryParams = new URLSearchParams();
      
      if (SC_ID) {
        queryParams.set("isNewlyCreated", "No");
        if (updatedScopeCertificateData?.scopeCertificateDetails?.scopeCertificateNo != existingSCData?.scopeCertificateDetails?.scopeCertificateNo || updatedScopeCertificateData?.scopeCertificateDetails?.scVersionNo != existingSCData?.scopeCertificateDetails?.scVersionNo) {
          queryParams.set("oldScNo", existingSCData.scopeCertificateDetails.scopeCertificateNo);
          queryParams.set("oldScVersionNo", existingSCData.scopeCertificateDetails.scVersionNo);
        }
      }
      else {
        queryParams.set("isNewlyCreated", "Yes");
      }
      const finalUrl = `${API_ROUTES.POST_SC_DETAILS_V4}?${queryParams}`;
      const res = await executePost(finalUrl, payload);

      if (res?.status === 200) {
        Utills.cl(res.data.scId);
        notify("Saved Succesfully", "success");
        await onNextTab();
        navigate(
          `/v4/sc/${formtype}/certified-organization-details/${res.data.scId}`
        );
      } else {
        notify(res?.data?.Failed, 'error');
      }
    } catch (error: any) {
      console.error("Error posting scope certificate details:", error);
      
      // Handle 409 Conflict error - Data already exists
      if (error?.response?.status === 409) {
        notify('Data Already exists in system', 'error');
        // Set active step to 1 (second tab)
        setActiveStep(0);
      } else {
        notify('An error occurred while saving the certificate details', 'error');
      }
    }
  };

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));


  const steps = [
    {
      label: "Scope Certificate Information",
      content: (
        <ScopeCertificateDetailsForm
          formRef={scopeDetailsRef}
          detailsData={scopeCertificateData.scopeCertificateDetails}
          setDetailsData={(data) =>
            handleDataUpdate("scopeCertificateDetails", data)
          }
          SC_ID={SC_ID}
          isAmendment={editSCInfo?.isParentStatus === true && formtype!=='create-sc'}
          scValidFromDate={scopeCertificateData.scopeCertificateDates.scValidFromDate}
          scStatus={scopeCertificateData.scopeCertificateDates.scStatus}
          hasPR0035={hasPR0035}
          hasFarmProcessCodes={hasFarmProcessCodes}
          isSuspended={isSuspended}
          isFarmSC={isFarmSC}
          setIsFarmSC={setIsFarmSC}
        />
      ),
    },
    {
      label: "Certificate Dates & Accreditation Information",
      content: (
        <ScopeCertificateDatesForm
          formRef={certificateDatesRef}
          datesData={scopeCertificateData.scopeCertificateDates}
          setDatesData={(data) =>
            handleDataUpdate("scopeCertificateDates", data)
          }
          scStatus={scopeCertificateData.scopeCertificateDates.scStatus}
          isAmendment={editSCInfo?.isParentStatus === true && formtype!=='create-sc'}
          isSuspended={isSuspended}
          facilityMinAuditDate={facilityMinAuditDate}
          facilityMaxAuditDate={facilityMaxAuditDate}
        />
      ),
    },
    {
      label: "Standard Information",
      content: (
        <StandardDetailsForm
          standardData={scopeCertificateData.standardDetails}
          setStandardData={(data) => handleDataUpdate("standardDetails", data)}
          scStatus={scopeCertificateData.scopeCertificateDates.scStatus}
          ccsFlag={scopeCertificateData.scopeCertificateDetails.ccsFlag}
          scValidFromDate={scopeCertificateData.scopeCertificateDates.scValidFromDate}
          scWithdrawalDate={scopeCertificateData.scopeCertificateDates.scWithdrawalDate}
          isSuspended={isSuspended}
        />
      ),
    },
    {
      label: "Non-mandatory Criterion Information",
      content: (
                 <NonMandatoryCriterionDetailsForm
           nonMandatoryCriterionData={scopeCertificateData.scNonMandatoryCriterionInformation}
           setNonMandatoryCriterionData={(data) => {
             handleDataUpdate("scNonMandatoryCriterionInformation", data);
           }}
         />
      ),
    },
  ];

  // Function to navigate to next tab
  const handleNavigateToNextTab = () => {
    navigate(`/v4/sc/${formtype}/certified-organization-details/${SC_ID}`);
  };

  const actions: ActionButtonConfig[] = [
   
    ...(
      // activeStep === 0 &&
       editSCInfo?.isParentStatus === true && formtype!=='create-sc' ? [
      {
        label: "Cancel Amendment",
        onClick: () => {
          setDialogOpen(true);
          setPopUpTitle("Cancel Amendment");
          setPopUpMessage("Are you sure you want to cancel this amendment?");
          setPopUpBtnName("Confirm");
        },
        variant: "contained" as const,
        color: "secondary" as const,
      }
    ] : []),
    {
      label:"Home",
      onClick: ()=>{
        navigate('/v4/sc-dashboard');
      },
      variant: "contained",
      color: "default",

    },
    ...(activeStep!==0?[{
      label: "Back",
      onClick: handleBack,
      variant: "contained" as const,
      color: "default" as const,
      disabled: activeStep === 0,
    }]:[]),
    {
      label: activeStep === steps.length - 1
        ? hasSubmitSC
          ? "Next"
          : "Save & Next"
        : "Next",
      onClick: activeStep === steps.length - 1
        ? hasSubmitSC
          ? handleNavigateToNextTab
          : handleFinish
        : handleNext,
      disabled:activeStep==1 && isRestrictPopUpSave,
      variant: "contained",
      color: "primary",
    },
  ];

  const handleCancelSave = () => {
    setDialogOpen(false);
  };

  const handleConfirm = async () =>{
    if(popUpTitle==="Warning"){
      setDialogOpen(false);
      if(formRefs[activeStep]?.current){
        const errors = await formRefs[activeStep].current.validateForm();
    
        await formRefs[activeStep].current.submitForm();
        if (Object.keys(errors).length === 0) {
          // Cross-step YYMM check — same gate as in handleNext for step 1
          if (activeStep === 1) {
            const yymmError = checkYymmConsistency(
              scopeCertificateData.scopeCertificateDetails?.scopeCertificateNo,
              formRefs[activeStep].current.values?.scValidFromDate
            );
            if (yymmError) {
              notify(yymmError, 'error');
              return;
            }
          }
          setActiveStep((prev) => Math.min(prev + 1, steps.length - 1)); // Move to the next step
        } else {
          notify('Please Enter the Required Values', 'error');
        }
       }
    }else{
      handleCancelAmendment();
    }

  }
  const handleCancelAmendment = async () => {
    try {
      await executePost(
        API_ROUTES.GET_SC_CANCEL_AMEND_V4,
        { scId: SC_ID, scCbLicensingCode: cbLicensingCode }
      );
      notify("Cancelled Amendment Successfully", "success");
    } catch (err) {
      console.error("Failed to cancel amendment", err);
    }
    navigate(`/v4/sc-dashboard`);
  };

  return (
    <>
      {" "}
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            onStepChange={(step) => setActiveStep(step)}
          />

          <div style={{ marginTop: "3rem" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              {actions.map((action, index) => (
                <StyledButton
                  key={index}
                  onClick={action.onClick}
                  variant={action.variant || "text"}
                  //   color={action.color || 'primary'}
                  disabled={action.disabled}
                >
                  {action.label}
                </StyledButton>
              ))}
            </Box>
          </div>
          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleConfirm}
            confirmText={popUpBtnName}
            cancelText="Cancel"
          />
        </>
      )}
    </>
  );
};

export default ScopeCertificateComponent;
